import React from 'react';
import { Cell, Label, LabelList, Pie, PieChart } from 'recharts';
import { ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';

interface ChartDataPoint {
  key: string;
  value: number;
  fill: string;
}

interface PieGraphProps {
  title?: string;
  titleValue?: string;
  chartConfig: ChartConfig;
  chartData: ChartDataPoint[];
  variant?: string;
  className?: string;
  outerRadius?: number;
  minHeight?: number;
  hideLabel?: boolean;
}

// Custom formatter for labels - hides 0% values
const customLabelFormatter = (value: number) => {
  return value === 0 ? '' : `${value}%`;
};

const PieGraph: React.FC<PieGraphProps> = ({
  title,
  titleValue,
  chartConfig,
  chartData,
  variant = 'default',
  className = '',
  outerRadius = 68,
  minHeight = 180,
  hideLabel = false
}) => {
  // Check if only one data point has a non-zero value
  const hasOnlyOneValue = chartData.filter((item) => item.value > 0).length === 1;
  const singleValueItem = hasOnlyOneValue ? chartData.find((item) => item.value > 0) : null;

  // generating legend items based on chartData and chartConfig
  const legendItems = chartData.map((item) => ({
    key: item.key,
    label: chartConfig[item.key]?.label || item.key,
    color: chartConfig[item.key]?.color || item.fill
  }));

  console.log('chartData', chartData);
  console.log('', chartData);

  return (
    <div className={`${className}`}>
      {!hideLabel && (
        <>
          <p
            className={`${variant === 'primary' ? 'text-primary' : 'text-secondary'} text-xs md:text-sm font-medium md:leading-[17px]`}
          >
            {title}
          </p>
          <h3
            className={`${variant === 'primary' ? 'text-primary' : ''} font-bold text-xl md:text-[28px] leading-[24.3px] md:leading-[34px]`}
          >
            {titleValue}
          </h3>
        </>
      )}

      <ChartContainer config={chartConfig} className={`mx-auto aspect-square max-h-[327px] mt-1 md:h-[${minHeight}px]`}>
        <PieChart>
          <ChartTooltip cursor={false} content={<ChartTooltipContent hideLabel />} />
          <Pie
            data={chartData}
            dataKey="value"
            nameKey="key"
            className="border w-full"
            outerRadius={outerRadius}
            // innerRadius={hasOnlyOneValue ? 0 : undefined}
          >
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.fill} />
            ))}

            {hasOnlyOneValue ? (
              <Label
                value={`${singleValueItem?.value}%`}
                position="center"
                fill="#ffffff"
                style={{
                  fontSize: '16px',
                  fontWeight: 'medium',
                  textAnchor: 'middle',
                  dominantBaseline: 'middle'
                }}
              />
            ) : (
              <LabelList
                dataKey="value"
                className="fill-background"
                stroke="none"
                fontSize={12}
                formatter={customLabelFormatter}
              />
            )}
          </Pie>
        </PieChart>
      </ChartContainer>
      {!hideLabel && (
        <div className="pl-0 mt-[0.35rem] md:flex md:flex-col md:items-center">
          {legendItems.map(({ key, label, color }) => (
            <div key={key} className="flex items-center gap-[0.4rem] mb-[2px]">
              <div
                className="h-2 w-2 md:h-[9px] md:w-[9px] shrink-0 rounded-full"
                style={{
                  backgroundColor: color
                }}
              />
              <span className="text-xs md:text-sm text-secondary">{typeof label === 'string' ? label : key}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export type { ChartDataPoint, PieGraphProps };

export default PieGraph;
